import { inject, Injectable, NgZone } from '@angular/core';
import { Firestore, doc, onSnapshot } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { BlueprintUsage } from './blueprint.service';

export interface GenerationTask {
  pageName: string;
  sessionId: string;
  phase: string;
  message: string;
  status: 'generating' | 'completed' | 'failed';
  progress: number;
  startedAt: number;
  usage?: BlueprintUsage;
}

const STORAGE_KEY_PREFIX = 'ai_generation_tasks_';

@Injectable({
  providedIn: 'root',
})
export class AIGenerationTrackerService {
  private firestore = inject(Firestore);
  private zone = inject(NgZone);
  private unsubscribers: Map<string, () => void> = new Map();
  private currentClientId = '';

  tasks$ = new BehaviorSubject<Map<string, GenerationTask>>(new Map());

  private get storageKey(): string {
    return STORAGE_KEY_PREFIX + (this.currentClientId || 'unknown');
  }

  /**
   * Show a placeholder "preparing" card immediately on submit — before there's a
   * sessionId — so the user gets instant feedback while the request round-trips.
   * startTracking() later upgrades it to the real session (with the Firestore
   * subscription).
   */
  startPending(pageName: string): void {
    const task: GenerationTask = {
      pageName,
      sessionId: '',
      phase: '',
      message: 'Preparing…',
      status: 'generating',
      progress: 3,
      startedAt: Date.now(),
    };
    const tasks = this.tasks$.value;
    tasks.set(pageName, task);
    this.tasks$.next(new Map(tasks));
  }

  startTracking(pageName: string, sessionId: string, clientId: string): void {
    this.currentClientId = clientId;
    const task: GenerationTask = {
      pageName,
      sessionId,
      phase: '',
      message: 'Starting...',
      status: 'generating',
      progress: 5,
      startedAt: Date.now(),
    };
    const tasks = this.tasks$.value;
    tasks.set(pageName, task);
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
    this.subscribeToSession(pageName, sessionId, clientId);
  }

  updateProgress(pageName: string, phase: string, message: string): void {
    const tasks = this.tasks$.value;
    const task = tasks.get(pageName);
    if (!task) return;
    task.phase = phase;
    task.message = message;
    task.progress = this.phaseToProgress(phase);
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
  }

  setUsage(pageName: string, usage: BlueprintUsage): void {
    const tasks = this.tasks$.value;
    const task = tasks.get(pageName);
    if (!task) return;
    task.usage = usage;
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
  }

  markCompleted(pageName: string): void {
    const tasks = this.tasks$.value;
    const task = tasks.get(pageName);
    if (!task) return;
    task.status = 'completed';
    task.progress = 100;
    task.message = 'Done!';
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
    this.unsubscribeSession(pageName);
    setTimeout(() => this.removeTask(pageName), 3000);
  }

  markFailed(pageName: string, error: string): void {
    const tasks = this.tasks$.value;
    const task = tasks.get(pageName);
    if (!task) return;
    task.status = 'failed';
    task.message = error;
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
    this.unsubscribeSession(pageName);
  }

  removeTask(pageName: string): void {
    const tasks = this.tasks$.value;
    tasks.delete(pageName);
    this.tasks$.next(new Map(tasks));
    this.saveToStorage();
    this.unsubscribeSession(pageName);
  }

  getTask(pageName: string): GenerationTask | undefined {
    return this.tasks$.value.get(pageName);
  }

  restoreSubscriptions(clientId: string): void {
    this.currentClientId = clientId;
    this.loadFromStorage();
    const tasks = this.tasks$.value;
    for (const [pageName, task] of tasks) {
      if (task.status === 'generating') {
        this.subscribeToSession(pageName, task.sessionId, clientId);
      }
    }
  }

  private subscribeToSession(pageName: string, sessionId: string, clientId: string): void {
    this.unsubscribeSession(pageName);
    const ref = doc(this.firestore, `client/${clientId}/ai-sessions/${sessionId}`);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as any;
      this.zone.run(() => {
        const tasks = this.tasks$.value;
        const task = tasks.get(pageName);
        if (!task) return;

        if (data.status === 'completed') {
          this.markCompleted(pageName);
        } else if (data.status === 'failed') {
          this.markFailed(pageName, data.error || 'Generation failed');
        } else if (data.status === 'generating' && task.status !== 'generating') {
          task.status = 'generating';
          this.tasks$.next(new Map(tasks));
          this.saveToStorage();
        }
      });
    });
    this.unsubscribers.set(pageName, unsub);
  }

  private unsubscribeSession(pageName: string): void {
    const unsub = this.unsubscribers.get(pageName);
    if (unsub) {
      unsub();
      this.unsubscribers.delete(pageName);
    }
  }

  private phaseToProgress(phase: string): number {
    switch (phase) {
      case 'GENERATE': return 30;
      case 'CONVERT': return 60;
      case 'HEAL': return 70;
      case 'SAVE': return 90;
      default: return 10;
    }
  }

  private saveToStorage(): void {
    const obj: Record<string, GenerationTask> = {};
    for (const [key, val] of this.tasks$.value) {
      obj[key] = val;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(obj));
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const obj = JSON.parse(raw) as Record<string, GenerationTask>;
      const map = new Map<string, GenerationTask>();
      for (const [key, val] of Object.entries(obj)) {
        if (val.status === 'generating') {
          map.set(key, val);
        }
      }
      this.tasks$.next(map);
    } catch {
      localStorage.removeItem(this.storageKey);
    }
  }
}
